import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { AlertTriangle, ArrowUpRight, Paperclip } from 'lucide-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { api, apiClient, ApiError } from '@/lib/api-client';
import { errorMessage } from '@/lib/errors';
import { toast } from '@/components/ui/toast';
import { FieldHelp } from '@/components/forms/FieldHelp';
import { formatMoney } from '@/components/data-table/columns-helper';
import { formatMinorExact } from '@/lib/money';
import { RequestSourceBadge } from './RequestSourceBadge';
import type { Static } from '@b1fr0st/contracts/common';
import type { FinanceRequestSource } from '@b1fr0st/contracts/domain/finance-list';
import type { LimitKind } from '@b1fr0st/contracts/domain/approval-limits';

export type ActionKind = 'approve' | 'reject' | 'complete';

/** The four independent approval limits, as svc-admin files them. */
export type { LimitKind };

/** What the operator is about to authorise, in their own terms. */
export interface ActionSubject {
  /** Minor units. */
  amount: number;
  /**
   * `null` when the agent's currency has not resolved. The amount is then not
   * renderable: minor units without an exponent are 100× ambiguous, and the
   * whole point of this dialog is the human authorisation step (N6).
   */
  currency: string | null;
  /**
   * The record's brand. The ceiling a decision is measured by is that brand's
   * (ADR-0002), whatever node the operator is bound to, so the "your limit" line
   * asks for it by name; without it the answer is for the operator's own node.
   */
  agentId?: string | null;
  /** Resolved username where known; the drawer falls back to the member id. */
  member: string;
  channel?: string | null;
  /** Withdrawals: the frozen destination account, masked. */
  destination?: string | null;
  /**
   * Who filed the record, as svc-finance derives it.
   *
   * The queue behind this dialog shows it as a column, and the dialog is where
   * the decision is actually authorised — an operator who reads "entered by
   * staff" on the list and then confirms against a panel that does not repeat it
   * is authorising from memory. On a payout it is the maker-checker reading:
   * a makeup withdrawal has already debited the player before anyone approves.
   *
   * Optional, because a surface whose row does not carry it shows no badge
   * rather than an invented one.
   */
  source?: Static<typeof FinanceRequestSource> | null;
  /**
   * Withdrawals converted at request time: what the player receives, in
   * `payoutCurrency` minor units, at `convertRate` (payout units per ONE ledger
   * unit). `payoutCurrency` null = paid in the ledger currency. Mark-paid refuses
   * a house account in any other currency (FIN-372), and so does this dialog.
   */
  payoutAmount?: number | null;
  payoutCurrency?: string | null;
  convertRate?: string | null;
}

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /**
   * Base path with id, e.g. `/api/v1/withdraw-decisions/abc`. The money queues
   * serve their decisions from a bo-only resource of their own, which is not
   * the resource the row was listed from (`apps/api-gateway/src/upstreams.ts`).
   */
  pathBase: string | null;
  action: ActionKind;
  invalidateKey: string;
  /**
   * Ask which house account the money moved through: the source of a withdraw
   * payout, or the destination a deposit actually landed in.
   */
  askAccount?: boolean;
  /**
   * Which side of the ledger this dialog is on. It picks the moment the account
   * question is asked (withdraws at completion, deposits at approval), which
   * house accounts may answer it — an account flagged withdraw-only cannot have
   * received a deposit, and a deposit-only one cannot have sent a payout — and
   * which queue's copy the request-source badge reads, since `none` means
   * something different on each side.
   */
  accountUsage?: 'deposit' | 'withdraw';
  /**
   * Preselected account — for a deposit, the one the PLAYER said they paid into.
   * It is a claim, not a fact, which is the whole reason the operator can change
   * it here before the row goes terminal.
   */
  defaultAccountId?: string | null;
  /**
   * The row being acted on.
   *
   * The dialog used to render `pathBase.split('/').pop()` — the raw row UUID —
   * as its entire description, so an operator confirmed a money movement
   * against a 36-character hex string. The triggers are three 12px icon buttons
   * in a ten-column dense table with the amount four cells away, so a
   * one-row mis-click produced a dialog that looked identical for both rows and
   * nothing in the flow could catch it (BO-04).
   */
  subject?: ActionSubject | null;
  /**
   * A hazard this particular row carries that the rest of the dialog cannot
   * show. When set, the text is rendered as an alert and the confirm button
   * stays disabled until the operator ticks the box beside it — the confirm
   * click alone is then no longer enough to authorise the action.
   *
   * Pass null on rows that do not carry the hazard. The step exists to make one
   * dangerous row feel different from the ordinary ones next to it, and a
   * checkbox on every row is a checkbox nobody reads.
   */
  acknowledgement?: string | null;
  /**
   * Which ONE of the caller's four approval limits governs THIS decision, so
   * the dialog can show it before the click rather than after the refusal.
   *
   * Exactly one, by design — see {@link LIMIT_LABELS}. Omit on a surface whose
   * limit is not enforced yet: a ceiling shown beside a button that ignores it
   * is worse than none.
   */
  limitKind?: LimitKind;
  /**
   * Block the confirm until a remark is typed.
   *
   * Opt-in, and default OFF, because this one dialog serves several upstreams.
   * The deposit action route and all three withdraw routes — action, complete
   * and bulk-approve — refuse a blank remark for every caller
   * (FIN-332), and both queues pass this. svc-promo's claim action does not.
   * A dialog that demanded one where the server does not would be a
   * rule enforced in exactly the place it must NOT live — the UI — and it would
   * leave the direct caller untouched, so each surface turns this on in the
   * change that makes its own server refuse.
   */
  remarkRequired?: boolean;
  /**
   * Where a photograph of the transfer is uploaded, e.g.
   * `/api/v1/withdraw-proofs/<id>`. Offered only on the `complete` action: it
   * is the moment an operator has just sent the money and is holding the bank's
   * receipt, and the only moment the record can still grow one.
   *
   * Optional, and absent everywhere else. The path is passed in rather than
   * derived from `pathBase` because it is a DIFFERENT gateway resource — the
   * upload carries a twelve-megabyte body and its own rate-limit tier
   * (`apps/api-gateway/src/upstreams.ts`).
   */
  proofUploadPath?: string | null;
}

interface HouseAccount {
  id: string;
  bankCode: string;
  accountNumber: string;
  accountName: string;
  usage: string;
  /** null = the account pays in the brand ledger currency. */
  currency?: string | null;
}

interface MyLimit {
  kind: LimitKind;
  currency: string;
  /** Minor units as a decimal STRING — a 0-decimal ceiling can exceed 2^53. */
  maxAmount: string | null;
  unlimited: boolean;
  source: 'admin' | 'role' | 'none';
}

interface MyLimits {
  agentId: string;
  roleCode: string | null;
  currency: string;
  items: MyLimit[];
}

/**
 * What the record's refusal left behind. Rendered in place rather than only
 * toasted, because the outcome is not "that failed" — it is "the request has
 * moved up a rung and is no longer yours", which the operator has to read
 * before they close the dialog and wonder where the row went.
 */
interface Escalation {
  level: number | null;
  limit: string | null;
  amount: string | null;
  currency: string | null;
  noLimitOnRecord: boolean;
}

/**
 * ONE ceiling is shown, and it is the one that governs the button being pressed.
 *
 * That is the rule, not a shortfall of it. An operator holds four independent
 * limits; a dialog that listed all four beside a single Approve would be asking
 * them to work out which line applies at the moment they are deciding money,
 * and three of the four numbers would be about something else. The deposit and
 * withdraw ceilings are independent and it is routine for one to admit an
 * amount the other refuses, so the line names the kind it governs rather than
 * saying "your approval limit". All four together are a different question —
 * "what am I trusted with" — and the Approval Limits tab is where it is asked.
 *
 * Only the two decision kinds appear here: `adjust_add` / `adjust_sub` govern a
 * manual wallet movement, decided in the wallet dialog, which never opens this
 * one.
 */
const LIMIT_LABELS = {
  deposit: {
    amount: 'finance.myLimit',
    unlimited: 'finance.myLimitUnlimited',
    none: 'finance.myLimitNoneOnRecord',
  },
  withdraw: {
    amount: 'finance.myLimitWithdraw',
    unlimited: 'finance.myLimitWithdrawUnlimited',
    none: 'finance.myLimitWithdrawNoneOnRecord',
  },
} as const;

const LIMITS_RESOURCE = '/api/v1/approval-limits/me';

/** The channels a manual deposit can arrive by today (finance.deposits.channel). */
const DEPOSIT_CHANNELS = ['bank_transfer', 'promptpay'] as const;

/** Show enough of an account number to recognise, not enough to leak. */
function maskAccount(account: string): string {
  const digits = account.replace(/\s+/g, '');
  if (digits.length <= 4) return digits;
  return `${'•'.repeat(Math.max(0, digits.length - 4))}${digits.slice(-4)}`;
}

export function ActionDialog({
  open,
  onOpenChange,
  pathBase,
  action,
  invalidateKey,
  askAccount = false,
  accountUsage = 'withdraw',
  defaultAccountId = null,
  subject,
  acknowledgement = null,
  limitKind,
  remarkRequired = false,
  proofUploadPath = null,
}: Props) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [reason, setReason] = React.useState('');
  const [houseAccountId, setHouseAccountId] = React.useState('');
  const [channel, setChannel] = React.useState('');
  const [acknowledged, setAcknowledged] = React.useState(false);
  const [proofFile, setProofFile] = React.useState<File | null>(null);
  const [escalation, setEscalation] = React.useState<Escalation | null>(null);
  const [heldAbove, setHeldAbove] = React.useState<number | null>(null);
  // svc-finance FIN-392: the bank already paid for this request through a
  // bank-statement credit. Approving would pay it twice; the dialog offers the
  // one honest resolution instead — close it against that credit.
  const [alreadyCredited, setAlreadyCredited] = React.useState<string | null>(null);
  const needsAcknowledgement = typeof acknowledgement === 'string' && acknowledgement !== '';
  // The server refuses a blank remark for every caller on the surfaces that
  // opt in (FIN-332) — this only saves the operator the round trip.
  const remarkMissing = remarkRequired && reason.trim() === '';

  // A withdraw records its source when the transfer is marked paid; a deposit
  // records its destination at the moment of approval, because that is the last
  // point before the credit makes the row terminal.
  const wantsAccount =
    askAccount && (accountUsage === 'deposit' ? action === 'approve' : action === 'complete');

  // Marking the payout paid is the only act that can attach proof, and it is
  // optional: a transfer made before there was anywhere to put a photograph
  // cannot retroactively grow one, and a queue that refused to record a real
  // payout over a missing image would stop paying people.
  const wantsProof = proofUploadPath !== null && action === 'complete';

  // The ceiling is shown only where it can actually bite — `action === 'approve'`
  // and nothing else.
  //
  // Rejecting is never limited, and marking a payout paid is never
  // re-checked (the ladder decided at approval, `markCompleted`). A figure beside
  // either button is the thing this component's own `limitKind` doc warns about:
  // a ceiling next to a control that ignores it is worse than none, because the
  // operator reads it as the rule that is about to be applied.
  const wantsLimit = limitKind !== undefined && action === 'approve';
  // The ceiling is asked for in the RECORD's own currency, never the platform
  // default. Limits are stored per currency and svc-finance enforces in the
  // denomination the record carries, so a THB-keyed question on a LAK brand
  // reads rows that do not exist and answers "no limit on record" to an
  // operator who in fact holds an unlimited one — the screen and the server
  // disagreeing at the moment money is decided (§2.7).
  //
  // Withheld until the currency resolves, for the same reason the amount above
  // is: a ceiling rendered under a guessed exponent is 100x wrong on the one
  // line whose job is telling the operator what they may authorise.
  const limitCurrency = subject?.currency ?? null;
  const limitAgentId = subject?.agentId ?? null;
  const limitsQuery = useQuery<MyLimits>({
    queryKey: [LIMITS_RESOURCE, limitCurrency, limitAgentId],
    queryFn: () =>
      api.get<MyLimits>(
        `${LIMITS_RESOURCE}?${new URLSearchParams({
          currency: limitCurrency ?? '',
          ...(limitAgentId ? { agentId: limitAgentId } : {}),
        }).toString()}`,
      ),
    enabled: open && wantsLimit && limitCurrency !== null,
    staleTime: 60 * 1000,
    retry: false,
  });
  const myLimit = limitsQuery.data?.items.find((l) => l.kind === limitKind);
  // Asked for and not answered — the route refused, timed out, or came back
  // without this kind. Rendering nothing here made that identical on screen to
  // a dialog with no ceiling to show, and "no ceiling was read" is a different
  // fact from "there is no ceiling": ADR-0002 escalates on the second one.
  const limitUnreadable =
    wantsLimit && limitCurrency !== null && !limitsQuery.isPending && myLimit === undefined;
  const limitLabels = limitKind === 'withdraw' ? LIMIT_LABELS.withdraw : LIMIT_LABELS.deposit;

  const accountsQuery = useQuery<{ items: HouseAccount[] }>({
    queryKey: ['payout-bank-accounts'],
    queryFn: () =>
      api.listPaginated<HouseAccount>('/api/v1/operator/banks/bo', {
        pageSize: 100,
        isActive: 'true',
      }),
    enabled: open && wantsAccount,
    staleTime: 5 * 60 * 1000,
  });

  // An account flagged for the other direction cannot have moved this money, so
  // offering it would only let an operator record a source that never paid or a
  // destination that never received.
  const houseAccounts = (accountsQuery.data?.items ?? []).filter((a) =>
    accountUsage === 'deposit'
      ? a.usage === 'deposit' || a.usage === 'both'
      : a.usage === 'withdraw' || a.usage === 'both',
  );

  // A preselected id that is not on offer — the declared account has since been
  // deactivated or reflagged withdraw-only — must not survive as a hidden
  // selection: the trigger would render blank (Radix has no item to name) while
  // the confirm still submitted it. Fall back to the placeholder and make the
  // operator choose.
  const selectedAccountId = houseAccounts.some((a) => a.id === houseAccountId)
    ? houseAccountId
    : '';

  // Marking a payout paid is the ONLY moment the source account is asked for,
  // and it moves the row terminal — so a confirm with the selector untouched
  // wrote a completed withdrawal whose Channel Account cell is permanently
  // empty and whose bank statement line can never be matched back.
  //
  // Keyed on `accountUsage`, not on `wantsAccount`: the deposit side shares this
  // dialog and its account is the PLAYER's claim about where they paid, which
  // may legitimately be absent. Gating that too would stop approvals.
  const payoutAccountMissing =
    wantsAccount && accountUsage === 'withdraw' && selectedAccountId === '';
  // The agent has no withdraw-capable house account at all, so "choose one" is
  // an instruction the operator cannot follow — say what actually has to happen.
  const noPayableAccount =
    payoutAccountMissing && accountsQuery.isSuccess && houseAccounts.length === 0;

  // Mirrors svc-finance's FIN-372 check: a payout fixed in a currency may only be
  // recorded from an account in that currency; an unconverted one only from an
  // account in the ledger currency (or one that names none).
  const chosenAccount = houseAccounts.find((a) => a.id === selectedAccountId);
  const accountCurrency = chosenAccount?.currency ? chosenAccount.currency.toUpperCase() : null;
  const ledgerCurrency = subject?.currency ? subject.currency.toUpperCase() : null;
  const payoutCurrency = subject?.payoutCurrency ? subject.payoutCurrency.toUpperCase() : null;
  const accountCurrencyMismatch =
    wantsAccount &&
    accountUsage === 'withdraw' &&
    chosenAccount !== undefined &&
    (payoutCurrency !== null
      ? accountCurrency !== payoutCurrency
      : accountCurrency !== null && ledgerCurrency !== null && accountCurrency !== ledgerCurrency);

  React.useEffect(() => {
    if (open) {
      setReason('');
      setHouseAccountId(defaultAccountId ?? '');
      setChannel('');
      // Never carried over: the dialog is reused for every row in the table, so
      // a tick left standing would pre-acknowledge the NEXT row's hazard.
      setAcknowledged(false);
      // Never carried over, for the reason the acknowledgement tick is not: the
      // dialog serves every row in the table, and a file left standing would
      // attach THIS row's receipt to the next one.
      setProofFile(null);
      setEscalation(null);
      setHeldAbove(null);
      setAlreadyCredited(null);
    }
  }, [open, defaultAccountId]);

  const mutation = useMutation({
    mutationFn: async () => {
      if (!pathBase) throw new Error('no target');
      if (action === 'complete') {
        // The upload FIRST, and a failure here aborts the completion rather
        // than quietly marking the payout paid without the proof the operator
        // chose. Completion is terminal, so "it went through but the receipt
        // did not" is a row that can never be corrected.
        let proofRef: string | undefined;
        if (wantsProof && proofFile && proofUploadPath) {
          const form = new FormData();
          form.append('file', proofFile);
          // Not `api.post`: this request is multipart, and axios sets the
          // boundary itself only when no content-type is forced on it.
          const { data } = await apiClient.post<{ slipRef: string }>(proofUploadPath, form);
          proofRef = data.slipRef;
        }
        return api.post(`${pathBase}/complete`, {
          reason,
          ...(selectedAccountId ? { houseBankAccountId: selectedAccountId } : {}),
          ...(proofRef ? { proofRef } : {}),
        });
      }
      return api.post(`${pathBase}/action`, {
        action,
        reason,
        // Only on the deposit approve path. The withdraw `/action` route has no
        // field for these and — under Elysia's default `normalize: true` — does
        // NOT reject them: it strips the unknown properties and answers 200. So
        // widening the gate would not fail loudly, it would hand the operator a
        // success toast for an account the row never recorded. Silent discard on
        // a money path is the reason this is keyed on `accountUsage`.
        ...(wantsAccount && selectedAccountId ? { houseBankAccountId: selectedAccountId } : {}),
        ...(wantsAccount && channel ? { channel } : {}),
      });
    },
    onSuccess: () => {
      toast.success(t(`finance.${action}`));
      qc.invalidateQueries({ queryKey: [invalidateKey] });
      onOpenChange(false);
    },
    onError: (err) => {
      const code = err instanceof ApiError ? err.code : undefined;
      const ext = (err instanceof ApiError ? err.extensions : undefined) as
        | Record<string, unknown>
        | undefined;
      // FIN-330 is not a failure to retry: the record CHANGED — it now carries
      // an escalation badge and belongs to the next rung. The queue behind the
      // dialog is stale from this moment, so it is refreshed even though
      // nothing was approved.
      if (code === 'FIN-330') {
        setEscalation({
          level: typeof ext?.escalatedToLevel === 'number' ? ext.escalatedToLevel : null,
          limit: typeof ext?.limit === 'string' ? ext.limit : null,
          amount: typeof ext?.amount === 'string' ? ext.amount : null,
          currency: typeof ext?.currency === 'string' ? ext.currency : null,
          noLimitOnRecord: ext?.limitSource === 'none',
        });
        qc.invalidateQueries({ queryKey: [invalidateKey] });
        return;
      }
      if (code === 'FIN-331') {
        setHeldAbove(typeof ext?.escalatedToLevel === 'number' ? ext.escalatedToLevel : null);
        qc.invalidateQueries({ queryKey: [invalidateKey] });
        return;
      }
      if (code === 'FIN-392' && typeof ext?.creditedDepositId === 'string') {
        setAlreadyCredited(ext.creditedDepositId);
        return;
      }
      toast.error(errorMessage(err, t('errors.generic')));
    },
  });

  const closeAsCredited = useMutation({
    mutationFn: () => {
      if (!pathBase || !alreadyCredited) throw new Error('no target');
      return api.post(`${pathBase}/close-as-credited`, { creditedDepositId: alreadyCredited, reason });
    },
    onSuccess: () => {
      toast.success(t('finance.closedAsCredited'));
      qc.invalidateQueries({ queryKey: [invalidateKey] });
      onOpenChange(false);
    },
    onError: (err) => {
      toast.error(errorMessage(err, t('errors.generic')));
    },
  });

  const title =
    action === 'approve'
      ? t('finance.approve')
      : action === 'reject'
        ? t('finance.reject')
        : t('finance.markPaid');

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>
            {subject ? (
              <span className="flex flex-col gap-0.5">
                <span className="text-base font-semibold text-foreground bf-tnum">
                  {subject.currency
                    ? formatMoney(subject.amount, { currency: subject.currency })
                    : t('finance.currencyUnknown')}
                </span>
                <span>{subject.member}</span>
                {subject.payoutCurrency && subject.payoutAmount != null && (
                  <span className="bf-tnum">
                    {t('finance.payoutReceives', {
                      amount: formatMoney(subject.payoutAmount, { currency: subject.payoutCurrency }),
                    })}
                    {subject.convertRate && subject.currency
                      ? ` (${t('finance.statements.rateDirection', {
                          rate: subject.convertRate,
                          account: subject.payoutCurrency,
                          brand: subject.currency,
                        })})`
                      : ''}
                  </span>
                )}
                {subject.destination && <span className="bf-tnum">{maskAccount(subject.destination)}</span>}
                {subject.channel && <span>{subject.channel}</span>}
                {/* The same badge the queue renders, keyed on the side of the
                    ledger this dialog is on so `none` reads correctly on each. */}
                {subject.source && (
                  <span>
                    <RequestSourceBadge source={subject.source} queue={accountUsage} />
                  </span>
                )}
              </span>
            ) : (
              pathBase?.split('/').pop()
            )}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          {/* The decider sees their own ceiling BEFORE they click.
              Rendered only once it has resolved — "your limit is -" beside an
              approve button says less than nothing, and a ceiling without its
              currency is the 100x mistake §2.7 exists to stop. */}
          {wantsLimit && myLimit && (
            <p className="text-sm text-muted-foreground" data-testid="my-limit">
              {myLimit.source === 'none'
                ? t(limitLabels.none)
                : myLimit.unlimited || myLimit.maxAmount === null
                  ? t(limitLabels.unlimited)
                  : t(limitLabels.amount, {
                      // The ceiling comes off the wire as a decimal STRING and
                      // is rendered as one: it is the field on this console a
                      // float can reach the edge of.
                      amount: formatMinorExact(myLimit.maxAmount, myLimit.currency),
                    })}
            </p>
          )}
          {/* Said, and never a dead button. The server fails closed — an amount
              over the ceiling is escalated rather than approved (FIN-330) — so
              refusing to let the operator confirm would stop work the ladder
              already handles, over a display that could not be drawn. */}
          {limitUnreadable && (
            <p className="text-sm text-muted-foreground" data-testid="my-limit-unreadable">
              {t('finance.myLimitUnreadable')}
            </p>
          )}
          {needsAcknowledgement && (
            <div
              role="alert"
              className="flex gap-2 rounded-md border border-destructive/40 bg-destructive/10 p-3"
            >
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-destructive" aria-hidden="true" />
              <div className="space-y-2 text-sm">
                <p className="text-destructive">{acknowledgement}</p>
                <div className="flex items-start gap-2">
                  <Checkbox
                    checked={acknowledged}
                    onCheckedChange={setAcknowledged}
                    className="mt-0.5"
                    aria-label={t('common.acknowledge')}
                  />
                  <button
                    type="button"
                    className="text-left"
                    onClick={() => setAcknowledged((v) => !v)}
                  >
                    {t('common.acknowledge')}
                  </button>
                </div>
              </div>
            </div>
          )}
          {wantsAccount && (
            <div className="space-y-2">
              <Label htmlFor="payoutAccount" className="flex items-center gap-1.5">
                {accountUsage === 'deposit' ? t('finance.depositAccount') : t('finance.payoutAccount')}
                <FieldHelp
                  text={
                    accountUsage === 'deposit'
                      ? t('finance.depositAccountHelp')
                      : t('finance.payoutAccountHelp')
                  }
                />
              </Label>
              <Select value={selectedAccountId} onValueChange={setHouseAccountId}>
                <SelectTrigger id="payoutAccount">
                  <SelectValue
                    placeholder={
                      accountUsage === 'deposit'
                        ? t('finance.depositAccountPlaceholder')
                        : t('finance.payoutAccountPlaceholder')
                    }
                  />
                </SelectTrigger>
                <SelectContent>
                  {houseAccounts.map((a) => (
                    <SelectItem key={a.id} value={a.id}>
                      {a.bankCode} · {a.accountNumber} — {a.accountName}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {payoutAccountMissing && (
                <p role="alert" className="text-sm text-destructive">
                  {noPayableAccount
                    ? t('finance.payoutAccountNone')
                    : t('finance.payoutAccountRequired')}
                </p>
              )}
              {accountCurrencyMismatch && (
                <p role="alert" className="text-sm text-destructive">
                  {t('finance.payoutCurrencyMismatch', {
                    account: accountCurrency ?? ledgerCurrency ?? '-',
                    payout: payoutCurrency ?? ledgerCurrency ?? '-',
                  })}
                </p>
              )}
            </div>
          )}
          {wantsAccount && accountUsage === 'deposit' && (
            <div className="space-y-2">
              <Label htmlFor="depositChannel" className="flex items-center gap-1.5">
                {t('finance.channel')}
                <FieldHelp text={t('finance.channelKeepHelp')} />
              </Label>
              {/* Optional by design: leaving it untouched keeps whatever the
                  player's announcement recorded. Only an operator who saw the
                  money arrive by a different route should overwrite it. */}
              <Select value={channel} onValueChange={setChannel}>
                <SelectTrigger id="depositChannel">
                  <SelectValue placeholder={t('finance.channelKeep')} />
                </SelectTrigger>
                <SelectContent>
                  {DEPOSIT_CHANNELS.map((c) => (
                    <SelectItem key={c} value={c}>
                      {t(`finance.channelType.${c}`, c)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
          {wantsProof && (
            <div className="space-y-2">
              <Label htmlFor="payoutProof" className="flex items-center gap-1.5">
                {t('finance.payoutProof')}
                <FieldHelp text={t('finance.payoutProofHelp')} />
              </Label>
              <div className="flex items-center gap-2">
                <Paperclip className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden="true" />
                <input
                  id="payoutProof"
                  type="file"
                  accept="image/png,image/jpeg,image/webp,application/pdf"
                  className="text-sm file:mr-2 file:rounded-md file:border file:border-input file:bg-background file:px-2 file:py-1 file:text-sm"
                  onChange={(e) => setProofFile(e.target.files?.[0] ?? null)}
                />
              </div>
              <p className="text-xs text-muted-foreground">{t('finance.payoutProofOptional')}</p>
            </div>
          )}
          <div className="space-y-2">
            <Label htmlFor="reason" className="flex items-center gap-1.5">
              {remarkRequired ? t('finance.remark') : t('finance.reason')}
              <FieldHelp text={remarkRequired ? t('finance.remarkHelp') : t('common.help.reason')} />
            </Label>
            <Textarea
              id="reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={3}
              aria-required={remarkRequired}
            />
            {remarkMissing && (
              <p role="alert" className="text-sm text-destructive">
                {t('finance.remarkRequired')}
              </p>
            )}
          </div>
          {escalation && (
            <div
              role="alert"
              className="flex gap-2 rounded-md border border-amber-500/40 bg-amber-500/10 p-3 text-sm"
            >
              <ArrowUpRight className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
              <div className="space-y-1">
                <p className="font-medium">
                  {escalation.level === null
                    ? t('finance.escalatedUnknownLevel')
                    : t('finance.escalatedToLevel', { level: escalation.level })}
                </p>
                {/* The ceiling is shown only when its DENOMINATION came back
                    with it. `?? 'THB'` here would print a baht figure for a
                    brand settled in anything else — a wrong number presented as
                    the reason money did not move (§2.7). When the currency is
                    unresolved the escalation still reads: the level above has
                    it, which is what the operator has to act on. */}
                {escalation.noLimitOnRecord ? (
                  <p className="text-muted-foreground">
                    {t('finance.escalatedNoLimitOnRecord')}
                  </p>
                ) : escalation.currency ? (
                  <p className="text-muted-foreground">
                    {t('finance.escalatedOverLimit', {
                      limit: formatMinorExact(escalation.limit, escalation.currency),
                    })}
                  </p>
                ) : (
                  <p className="text-muted-foreground">
                    {t('finance.escalatedOverLimitUnknownCeiling')}
                  </p>
                )}
              </div>
            </div>
          )}
          {heldAbove !== null && (
            <div role="alert" className="rounded-md border border-destructive/40 bg-destructive/10 p-3 text-sm text-destructive">
              {t('finance.heldAboveYourLevel', { level: heldAbove })}
            </div>
          )}
          {alreadyCredited !== null && (
            <div
              role="alert"
              className="space-y-2 rounded-md border border-amber-500/40 bg-amber-500/10 p-3 text-sm"
            >
              <p className="font-medium">{t('finance.alreadyCredited')}</p>
              <p className="text-muted-foreground">
                {t('finance.alreadyCreditedHelp', { deposit: alreadyCredited.slice(0, 8) })}
              </p>
              <Button
                size="sm"
                variant="outline"
                onClick={() => closeAsCredited.mutate()}
                disabled={closeAsCredited.isPending || remarkMissing}
              >
                {t('finance.closeAsCredited')}
              </Button>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            {t('common.cancel')}
          </Button>
          {/* An operator cannot authorise an amount they were not shown, so the
              amount above hides itself until the currency resolves (N6).
              REJECT is not gated on it: refusing a payout needs no knowledge of
              its denomination, and the currency lookup is `GET /organizations`,
              which `operator` and `support` could not read at all — so gating
              BOTH buttons on it disabled the entire job of the role until the
              grant landed (W1). Approve stays gated; a stuck approve is a
              degraded queue, a wrong approve is money out the door.

              `acknowledgement` gates every action including reject, and that is
              deliberate: it is supplied per row, only for the rows that carry
              the hazard, and it is the operator's own tick that clears it. It
              never leaves an action unreachable. */}
          <Button
            variant={action === 'reject' ? 'destructive' : 'default'}
            onClick={() => mutation.mutate()}
            disabled={
              mutation.isPending ||
              // Once refused as already credited, approving again can only be
              // refused again; the panel above holds the way forward.
              alreadyCredited !== null ||
              (needsAcknowledgement && !acknowledged) ||
              payoutAccountMissing ||
              accountCurrencyMismatch ||
              (action !== 'reject' && subject != null && subject.currency == null) ||
              // The fifth condition, and the only one that applies to every
              // action: no decision without a remark.
              remarkMissing
            }
          >
            {title}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
